import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Collapse, Modal } from "react-bootstrap";
import PlaceBetOpenBets from "./PlaceBetOpenBets";
import MatchRulesModal from "./MatchRulesModal";

import {
  calculateCashout,
  fetchOpenBetsAsync,
  getBalanceCRefAsync,
  placeMatchOddsBetAsync,
} from "../../store/slices/betSlice";
import { toast } from "react-toastify";
import { fetchTeamPositionAsync } from "../../store/slices/gameSlice";
import { toggleFavoriteMarket } from "../../store/slices/sportEventSlice";
import { fetchMultiMarketDetails } from "../../store/slices/multiMarketSlice";

const DEFAULT_RUNNERS = [
  {
    name: "Melbourne City",
    volumeText: "(3000.00)",
    volumeClass: "text-green",
    backPrices: ["7.2", "7.4", "7.6"],
    backSizes: ["206.49", "141.41", "27.68"],
    layPrices: ["8", "8.2", "8.4"],
    laySizes: ["30.26", "101.07", "209.78"],
  },
  {
    name: "Buriram Utd",
    volumeText: "(-2000.00)",
    volumeClass: "text-red",
    backPrices: ["1.68", "1.69", "1.7"],
    backSizes: ["193.48", "319.66", "105.07"],
    layPrices: ["1.73", "1.74", "1.75"],
    laySizes: ["121.6", "274.64", "240.55"],
  },
  {
    name: "The Draw",
    volumeText: "(-2000.00)",
    volumeClass: "text-red",
    backPrices: ["3.35", "3.4", "3.45"],
    backSizes: ["179.37", "8", "95.64"],
    layPrices: ["3.55", "3.6", "3.65"],
    laySizes: ["147.57", "104.54", "9.11"],
  },
];

const useFlashOnChange = (value, duration = 400) => {
  const [flash, setFlash] = React.useState(false);
  // Store a serialized snapshot so in-place mutations of nested arrays
  // (common with odds feeds) still register as a change.
  const prevRef = React.useRef(JSON.stringify(value));

  React.useEffect(() => {
    const nextSerialized = JSON.stringify(value);
    if (prevRef.current === nextSerialized) {
      return;
    }

    prevRef.current = nextSerialized;
    setFlash(true);
    const id = setTimeout(() => setFlash(false), duration);
    return () => clearTimeout(id);
  }, [value, duration]);

  return flash;
};

const RunnerRow = ({
  runner,
  marketTitle,
  minStake,
  maxStake,
  onOddsClick,
  selectedBet,
  matchId,
}) => {
  const flash = useFlashOnChange({
    backPrices: runner.backPrices,
    backSizes: runner.backSizes,
    layPrices: runner.layPrices,
    laySizes: runner.laySizes,
  });

  const back0 = Number(runner?.backPrices?.[0]);
  const back1 = Number(runner?.backPrices?.[1]);
  const back2 = Number(runner?.backPrices?.[2]);

  const lay0 = Number(runner?.layPrices?.[0]);
  const lay1 = Number(runner?.layPrices?.[1]);
  const lay2 = Number(runner?.layPrices?.[2]);

  // Consider odds invalid when missing, "-", NaN, or <= 0.
  const canBack0 = Number.isFinite(back0) && back0 > 0;
  const canBack1 = Number.isFinite(back1) && back1 > 0;
  const canBack2 = Number.isFinite(back2) && back2 > 0;

  const canLay0 = Number.isFinite(lay0) && lay0 > 0;
  const canLay1 = Number.isFinite(lay1) && lay1 > 0;
  const canLay2 = Number.isFinite(lay2) && lay2 > 0;

  // Compact odds towards the middle:
  // Back side: center is slot 2 -> fill slot 2, then 1, then 0.
  // Lay side: center is slot 0 -> fill slot 0, then 1, then 2.
  const backItems = [];
  if (canBack0) {
    backItems.push({
      price: back0,
      displayPrice: runner.backPrices?.[0],
      displaySize: runner.backSizes?.[0],
    });
  }
  if (canBack1) {
    backItems.push({
      price: back1,
      displayPrice: runner.backPrices?.[1],
      displaySize: runner.backSizes?.[1],
    });
  }
  if (canBack2) {
    backItems.push({
      price: back2,
      displayPrice: runner.backPrices?.[2],
      displaySize: runner.backSizes?.[2],
    });
  }

  const backSlots = [null, null, null];
  const backSlotOrder = [2, 1, 0];
  backItems.forEach((item, idx) => {
    backSlots[backSlotOrder[idx]] = item;
  });

  const layItems = [];
  if (canLay0) {
    layItems.push({
      price: lay0,
      displayPrice: runner.layPrices?.[0],
      displaySize: runner.laySizes?.[0],
    });
  }
  if (canLay1) {
    layItems.push({
      price: lay1,
      displayPrice: runner.layPrices?.[1],
      displaySize: runner.laySizes?.[1],
    });
  }
  if (canLay2) {
    layItems.push({
      price: lay2,
      displayPrice: runner.layPrices?.[2],
      displaySize: runner.laySizes?.[2],
    });
  }

  const laySlots = [null, null, null];
  const laySlotOrder = [0, 1, 2];
  layItems.forEach((item, idx) => {
    laySlots[laySlotOrder[idx]] = item;
  });

  let potentialProfit = null;
  let stakeLiability = null;
  const isBookmakerMarket = String(marketTitle || "")
    .toUpperCase()
    .includes("BOOKMAKER");

  // const isLayBet = selectedBet && selectedBet?.side === "lay";
  if (selectedBet && String(selectedBet.marketId) === String(runner.marketId)) {
    const stakeNum = Number(selectedBet.stake);
    const oddsNum = Number(selectedBet.odds);
    const isSameSelection =
      String(selectedBet.selectionId) === String(runner.selectionId);

    if (
      Number.isFinite(stakeNum) &&
      Number.isFinite(oddsNum) &&
      stakeNum > 0 &&
      oddsNum > 0
    ) {
      const betSide = String(selectedBet.side || "").toLowerCase();
      // Bookmaker odds are percentage based (e.g. 350 => 3.50x profit),
      // while other markets use standard decimal odds.
      const normalizedOdds = isBookmakerMarket ? oddsNum / 100 : oddsNum;
      const backProfit = isBookmakerMarket
        ? normalizedOdds * stakeNum
        : normalizedOdds * stakeNum - stakeNum;
      const layLiability = isBookmakerMarket
        ? normalizedOdds * stakeNum
        : (normalizedOdds - 1) * stakeNum;

      if (betSide === "back") {
        if (isSameSelection) {
          // Back on selected runner: selected gets profit.
          potentialProfit = backProfit;
        } else {
          // Other runners carry stake liability.
          stakeLiability = -stakeNum;
        }
      } else if (betSide === "lay") {
        if (isSameSelection) {
          // Lay on selected runner: selected carries liability.
          stakeLiability = -layLiability;
        } else {
          // Other runners get stake as profit.
          potentialProfit = stakeNum;
        }
      }
    }
  }

  const runnerPl = Number.isFinite(runner.userPl) ? runner.userPl : 0;
  const currentBetImpact = potentialProfit ?? stakeLiability ?? 0;
  const totalPl = runnerPl + currentBetImpact;

  // Show P/L whenever we have data for this runner (team position or an
  // active bet impact), even if the final value is exactly 0.00. This lets
  // scenarios like one team ending up at 0.00 still be visible instead of
  // being hidden by a strict non-zero check.
  const hasPlData =
    Number.isFinite(runner.userPl) ||
    potentialProfit !== null ||
    stakeLiability !== null;
  const showPl = hasPlData;
  const displayPl = Number(totalPl.toFixed(2));
  const isSelectedRow =
    !!selectedBet &&
    !selectedBet?.isFancy &&
    String(selectedBet?.marketId) === String(runner?.marketId) &&
    String(selectedBet?.selectionId) === String(runner?.selectionId);

  return (
    <React.Fragment>
      <div
        className={`row mx-0 align-items-center${
          flash ? " odds-flash-row" : ""
        }`}
      >
        <div className="col-lg-6 col-md-5 col-8 px-0">
          <div className="d-flex flex-column px-1">
            <div className="d-flex justify-content-between align-items-center w-100">
              <span className="marketevent">{runner.name}</span>

              {showPl && (
                <span
                  className={`mrkt-volume ${
                    displayPl < 0
                      ? "text-red"
                      : displayPl > 0
                        ? "text-green"
                        : ""
                  }`}
                  style={{
                    paddingLeft: "5px",
                  }}
                >
                  ({displayPl.toFixed(2)})
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-6 col-md-7 col-4 px-0">
          {runner.isSuspended ? (
            <div className="btn-group w-100 match-odds-wrapper flex justify-content-center align-items-center border-right-red-500 border-left-red-500">
              <div className="fancy-status-text py-2 text-red-500">
                suspended
              </div>
            </div>
          ) : (
            <div className="btn-group">
              <button
                className={`back back-light d-md-block d-none${
                  backSlots[0] ? "" : " no-odds"
                }`}
                type="button"
                disabled={!backSlots[0] || !onOddsClick}
                onClick={() => {
                  if (!backSlots[0] || !onOddsClick) return;
                  onOddsClick({
                    selectionName: runner.name,
                    marketTitle,
                    side: "back",
                    minStake,
                    maxStake,
                    odds: backSlots[0].price,
                    marketId: runner.marketId,
                    selectionId: runner.selectionId,
                  });
                }}
              >
                <span>{backSlots[0]?.displayPrice ?? "0"}</span>
                <small>{backSlots[0]?.displaySize ?? "0"}</small>
              </button>
              <button
                className={`back back-light d-md-block d-none${
                  backSlots[1] ? "" : " no-odds"
                }`}
                type="button"
                disabled={!backSlots[1] || !onOddsClick}
                onClick={() => {
                  if (!backSlots[1] || !onOddsClick) return;
                  onOddsClick({
                    selectionName: runner.name,
                    marketTitle,
                    side: "back",
                    minStake,
                    maxStake,
                    odds: backSlots[1].price,
                    marketId: runner.marketId,
                    selectionId: runner.selectionId,
                  });
                }}
              >
                <span>{backSlots[1]?.displayPrice ?? "0"}</span>
                <small>{backSlots[1]?.displaySize ?? "0"}</small>
              </button>
              <button
                className={`back${backSlots[2] ? "" : " no-odds"}`}
                type="button"
                disabled={!backSlots[2] || !onOddsClick}
                onClick={() => {
                  if (!backSlots[2] || !onOddsClick) return;
                  onOddsClick({
                    selectionName: runner.name,
                    marketTitle,
                    side: "back",
                    minStake,
                    maxStake,
                    odds: backSlots[2].price,
                    marketId: runner.marketId,
                    selectionId: runner.selectionId,
                  });
                }}
              >
                <span>{backSlots[2]?.displayPrice ?? "0"}</span>
                <small>{backSlots[2]?.displaySize ?? "0"}</small>
              </button>
              <button
                className={`lay${laySlots[0] ? "" : " no-odds"}`}
                type="button"
                disabled={!laySlots[0] || !onOddsClick}
                onClick={() => {
                  if (!laySlots[0] || !onOddsClick) return;
                  onOddsClick({
                    selectionName: runner.name,
                    marketTitle,
                    side: "lay",
                    minStake,
                    maxStake,
                    odds: laySlots[0].price,
                    marketId: runner.marketId,
                    selectionId: runner.selectionId,
                  });
                }}
              >
                <span>{laySlots[0]?.displayPrice ?? "0"}</span>
                <small>{laySlots[0]?.displaySize ?? "0"}</small>
              </button>
              <button
                className={`d-md-block d-none lay lay-light${
                  laySlots[1] ? "" : " no-odds"
                }`}
                type="button"
                disabled={!laySlots[1] || !onOddsClick}
                onClick={() => {
                  if (!laySlots[1] || !onOddsClick) return;
                  onOddsClick({
                    selectionName: runner.name,
                    marketTitle,
                    side: "lay",
                    minStake,
                    maxStake,
                    odds: laySlots[1].price,
                    marketId: runner.marketId,
                    selectionId: runner.selectionId,
                  });
                }}
              >
                <span>{laySlots[1]?.displayPrice ?? "0"}</span>
                <small>{laySlots[1]?.displaySize ?? "0"}</small>
              </button>
              <button
                className={`d-md-block d-none lay lay-light${
                  laySlots[2] ? "" : " no-odds"
                }`}
                type="button"
                disabled={!laySlots[2] || !onOddsClick}
                onClick={() => {
                  if (!laySlots[2] || !onOddsClick) return;
                  onOddsClick({
                    selectionName: runner.name,
                    marketTitle,
                    side: "lay",
                    minStake,
                    maxStake,
                    odds: laySlots[2].price,
                    marketId: runner.marketId,
                    selectionId: runner.selectionId,
                  });
                }}
              >
                <span>{laySlots[2]?.displayPrice ?? "0"}</span>
                <small>{laySlots[2]?.displaySize ?? "0"}</small>
              </button>
            </div>
          )}
        </div>
      </div>
      {isSelectedRow && (
        <div className="col-12 px-0 mt-1 d-lg-none">
          <PlaceBetOpenBets
            inline
            showOpenBets={false}
            matchId={matchId}
            marketId={runner?.marketId}
          />
        </div>
      )}
    </React.Fragment>
  );
};

const MatchOddsSection = ({
  title = "Match Odds",
  minMaxText = "Min: 100 | Max: 25k",
  minStake = null,
  maxStake = null,
  collapseId = "Match_Odds",
  runners = DEFAULT_RUNNERS,
  showCashout = false,
  showMarquee = false,
  marqueeText = "",
  onOddsClick,
  market = null,
  matchId,
  marketPositions = [],
  marketId = null,
  initialBookmarked,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(true);
  const [showRules, setShowRules] = React.useState(false);
  const [pendingCashout, setPendingCashout] = React.useState(null); // { payload, profit }
  const [showCashoutPopup, setShowCashoutPopup] = React.useState(false);
  const [isProcessingCashout, setIsProcessingCashout] = React.useState(false);
  const selectedBet = useSelector((state) => state.game.selectedBet);
  const rulesSectionHeading = String(title || "")
    .toUpperCase()
    .includes("BOOKMAKER")
    ? "Bookmaker"
    : "Match";

  const bookmarkMarketId = marketId || collapseId;
  const [isBookmarked, setIsBookmarked] = React.useState(() => {
    if (typeof initialBookmarked === "boolean") return initialBookmarked;
    const v =
      market?.is_favourite ??
      market?.is_favorite ??
      market?.favorite ??
      market?.isFav ??
      false;
    return Boolean(Number(v) || v === true);
  });
  const [pendingBookmarkValue, setPendingBookmarkValue] = React.useState(null);

  React.useEffect(() => {
    if (pendingBookmarkValue !== null) return;

    if (typeof initialBookmarked === "boolean") {
      setIsBookmarked(initialBookmarked);
      return;
    }
    const v =
      market?.is_favourite ??
      market?.is_favorite ??
      market?.favorite ??
      market?.isFav ??
      false;
    setIsBookmarked(Boolean(Number(v) || v === true));
  }, [initialBookmarked, market, pendingBookmarkValue]);

  React.useEffect(() => {
    if (pendingBookmarkValue === null) return;

    const serverRaw =
      typeof initialBookmarked === "boolean"
        ? initialBookmarked
        : (market?.is_favourite ??
          market?.is_favorite ??
          market?.favorite ??
          market?.isFav ??
          false);
    const serverValue = Boolean(Number(serverRaw) || serverRaw === true);

    if (serverValue === pendingBookmarkValue) {
      setPendingBookmarkValue(null);
      setIsBookmarked(serverValue);
    }
  }, [initialBookmarked, market, pendingBookmarkValue]);

  const handleBookmarkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!bookmarkMarketId || !matchId) return;
    const next = !isBookmarked;
    setPendingBookmarkValue(next);
    setIsBookmarked(next);

    // Server-side favorite toggle (lotus-style behaviour)
    dispatch(
      toggleFavoriteMarket({
        eventId: bookmarkMarketId,
        matchId: matchId,
        isAdd: next,
        isFancy: false,
      }),
    )
      .unwrap()
      .then(() => {
        dispatch(fetchMultiMarketDetails());
        toast.success(next ? "Added to favorites" : "Removed from favorites");
      })
      .catch((err) => {
        setPendingBookmarkValue(null);
        setIsBookmarked((prev) => !prev);
        toast.error(
          err?.message || err?.msg || "Failed to update favorite on server",
        );
      });
  };

  const cashoutPreview = React.useMemo(() => {
    const result = calculateCashout(market, marketPositions);
    const payload = result?.payload ?? null;
    const profitNum =
      result?.profit == null || !Number.isFinite(Number(result.profit))
        ? null
        : Number(result.profit);
    const stakeNum =
      payload?.stack == null || !Number.isFinite(Number(payload.stack))
        ? null
        : Number(payload.stack);

    const runnersBefore = Array.isArray(result?.runners?.before)
      ? result.runners.before
      : [];
    const runnersAfter = Array.isArray(result?.runners?.after)
      ? result.runners.after
      : [];
    const averageAfter =
      result?.runners?.averageAfter != null &&
      Number.isFinite(Number(result.runners.averageAfter))
        ? Number(result.runners.averageAfter)
        : null;

    return {
      result,
      payload,
      enabled: Boolean(payload),
      profit: profitNum,
      stake: stakeNum,
      runnersBefore,
      runnersAfter,
      averageAfter,
    };
  }, [market, marketPositions]);

  const handleCashout = async () => {
    // Recompute with debug enabled so we only log reasons on user action.
    const result = calculateCashout(market, marketPositions, { debug: true });
    if (!result || !result.payload) {
      toast.error("Cashout not available");
      return;
    }

    setPendingCashout({ payload: result.payload, profit: result.profit });
    setShowCashoutPopup(true);
  };

  const confirmCashout = async () => {
    if (!pendingCashout?.payload || isProcessingCashout) return;

    setIsProcessingCashout(true);
    try {
      const res = await dispatch(
        placeMatchOddsBetAsync(pendingCashout.payload),
      ).unwrap();
      if (res?.status) {
        toast.success(res?.msg || "Cashout successful!");
        setShowCashoutPopup(false);
        setPendingCashout(null);
        setTimeout(() => {
          if (matchId) {
            dispatch(fetchTeamPositionAsync({ matchId: String(matchId) }));
          }
          dispatch(getBalanceCRefAsync());
          dispatch(fetchOpenBetsAsync());
        }, 500);
      } else {
        toast.error(res?.msg || "Failed to place cashout bet");
        setShowCashoutPopup(false);
        setPendingCashout(null);
      }
    } catch (error) {
      toast.error(error || "Failed to place cashout bet");
      setShowCashoutPopup(false);
      setPendingCashout(null);
    } finally {
      setIsProcessingCashout(false);
    }
  };

  return (
    <div className="market_section">
      <p>
        <span className="mrkname">{title}</span>
        <button
          type="button"
          className="add-pin"
          aria-label={isBookmarked ? "Remove bookmark" : "Bookmark market"}
          aria-pressed={isBookmarked}
          onClick={handleBookmarkClick}
        >
          <i
            className={isBookmarked ? "bi bi-bookmark-fill" : "bi bi-bookmark"}
          />
        </button>
        {showCashout && (
          <button
            className="btn-cashout"
            onClick={handleCashout}
            disabled={isProcessingCashout}
            aria-disabled={!cashoutPreview.enabled}
            title={
              cashoutPreview.enabled
                ? ""
                : "Cashout available only for supported 2-runner markets"
            }
          >
            Cashout
          </button>
        )}
        <span className="min-max">
          {" "}
          {minMaxText}{" "}
          <button
            type="button"
            className="ms-2 game-rules-icon"
            aria-label="Match rules"
            onClick={(e) => {
              e.stopPropagation?.();
              setShowRules(true);
            }}
          >
            <span>
              <i className="bi bi-info-square-fill"></i>
            </span>
          </button>
          <button
            type="button"
            aria-label={open ? "Collapse market" : "Expand market"}
            aria-expanded={open}
            className="collapsed an-collapse"
            onClick={() => setOpen(!open)}
          />
        </span>
      </p>
      <MatchRulesModal
        show={showRules}
        onClose={() => setShowRules(false)}
        sectionHeading={rulesSectionHeading}
      />
      <Collapse in={open}>
        <div>
          <div className="row align-items-center oddsHeader">
            <div className="col-lg-6 col-md-5 col-8 px-0"></div>
            <div className="col-lg-6 col-md-7 col-4 px-0">
              <div className="btn-group">
                <button className="ex-bl d-none d-md-block"></button>
                <button className="ex-bl d-none d-md-block"></button>
                <button className="back">back</button>
                <button className="lay">lay</button>
                <button className="ex-bl d-none d-md-block"></button>
                <button className="ex-bl d-none d-md-block"></button>
              </div>
            </div>
          </div>
          {runners.map((runner) => (
            <RunnerRow
              key={runner.name}
              runner={runner}
              marketTitle={title}
              minStake={minStake}
              maxStake={maxStake}
              onOddsClick={onOddsClick}
              selectedBet={selectedBet}
              matchId={matchId}
            />
          ))}
          {showMarquee && marqueeText && (
            <div className="row mx-0 align-items-center">
              <div className="col-12 text-right px-0">
                <span className="msgs">
                  {/* <marquee>
                    <i className="mdi mdi-email mr-1"></i> {marqueeText}
                  </marquee> */}
                </span>
              </div>
            </div>
          )}
        </div>
      </Collapse>

      <Modal
        show={showCashoutPopup}
        onHide={() => {
          if (isProcessingCashout) return;
          setShowCashoutPopup(false);
          setPendingCashout(null);
        }}
        centered
      >
        <Modal.Header closeButton={!isProcessingCashout}>
          <Modal.Title>Confirm Cashout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="fw-semibold mb-1">Current Book:</div>
            <div className="border rounded p-2 bg-light">
              {cashoutPreview.runnersBefore.map((row, idx) => (
                <div
                  key={`${row.name || "runner"}-before-${idx}`}
                  className="d-flex justify-content-between small"
                >
                  <span>{row.name || `Runner ${idx + 1}`}:</span>
                  <span
                    className={
                      Number(row.pl) < 0
                        ? "text-danger"
                        : Number(row.pl) > 0
                          ? "text-success"
                          : ""
                    }
                  >
                    {Number(row.pl).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <div className="fw-semibold mb-1">After Cashout:</div>
            <div className="border rounded p-2 bg-light">
              {cashoutPreview.runnersAfter.map((row, idx) => (
                <div
                  key={`${row.name || "runner"}-after-${idx}`}
                  className="d-flex justify-content-between small"
                >
                  <span>{row.name || `Runner ${idx + 1}`}:</span>
                  <span
                    className={
                      Number(row.pl) < 0
                        ? "text-danger"
                        : Number(row.pl) > 0
                          ? "text-success"
                          : ""
                    }
                  >
                    {Number(row.pl).toFixed(2)}
                  </span>
                </div>
              ))}
              {cashoutPreview.averageAfter != null && (
                <div className="d-flex justify-content-between small mt-2 border-top pt-1">
                  <span>Average:</span>
                  <span
                    className={
                      cashoutPreview.averageAfter < 0
                        ? "text-danger"
                        : cashoutPreview.averageAfter > 0
                          ? "text-success"
                          : ""
                    }
                  >
                    {cashoutPreview.averageAfter.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mb-2">
            <div className="fw-semibold mb-1">Hedge Bet Details:</div>
            <div className="border rounded p-2">
              <div className="d-flex justify-content-between small">
                <span>Type:</span>
                <strong>
                  {pendingCashout?.payload?.is_back === 1
                    ? "BACK"
                    : pendingCashout?.payload?.is_back === 0
                      ? "LAY"
                      : "--"}
                </strong>
              </div>
              <div className="d-flex justify-content-between small">
                <span>Odds:</span>
                <strong>{pendingCashout?.payload?.odds ?? "--"}</strong>
              </div>
              <div className="d-flex justify-content-between small">
                <span>Stake:</span>
                <strong>{pendingCashout?.payload?.stack ?? "--"}</strong>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="bg-white text-dark border-1 px-3 "
            onClick={() => {
              setShowCashoutPopup(false);
              setPendingCashout(null);
            }}
            disabled={isProcessingCashout}
          >
            Cancel
          </button>
          <Button
            className="btn-cashout"
            onClick={confirmCashout}
            disabled={isProcessingCashout || !pendingCashout?.payload}
          >
            {isProcessingCashout ? "Processing..." : "Confirm Cashout"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MatchOddsSection;
